<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  addMinutes,
  differenceInMinutes,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isToday,
  isSameDay,
  isSameMonth,
  parseISO,
  addHours,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

const props = defineProps({
  view: { type: String, default: 'month' },
  anchorDate: { type: Date, required: true },
  selectedDate: { type: Date, default: null },
  bookings: { type: Array, default: () => [] },
  availability: { type: Array, default: () => [] },
  scheduleColor: { type: String, default: '#6366f1' },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'selectDate',
  'createBooking',
  'moveBooking',
  'editBooking',
]);

// ─── Grid constants ───────────────────────────────────────
const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const HOUR_START = 7;
const HOUR_END = 23;
const TOTAL_HOURS = HOUR_END - HOUR_START; // 16
const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => i + HOUR_START);

// ROW_HEIGHT must match the CSS h-20 = 5rem = 80px (at Tailwind default 1rem=16px)
const ROW_HEIGHT = 80;
const GRID_HEIGHT = TOTAL_HOURS * ROW_HEIGHT; // 1120px
// Snap to 15-min = 20px
const SNAP_PX = ROW_HEIGHT / 4;

// ─── Position helpers ─────────────────────────────────────
const getDayStart = day => addHours(startOfDay(day), HOUR_START);

const toTopPx = (isoString, day) => {
  try {
    const offset = Math.max(
      0,
      differenceInMinutes(parseISO(isoString), getDayStart(day))
    );
    return (offset / 60) * ROW_HEIGHT;
  } catch {
    return 0;
  }
};

const toHeightPx = booking => {
  try {
    const dur = Math.max(
      15,
      differenceInMinutes(
        parseISO(booking.ends_at),
        parseISO(booking.starts_at)
      )
    );
    return Math.max(28, (dur / 60) * ROW_HEIGHT);
  } catch {
    return 28;
  }
};

const pxToIso = (topPx, day) => {
  const snapped = Math.round(topPx / SNAP_PX) * SNAP_PX;
  const minutes = (snapped / ROW_HEIGHT) * 60;
  return addMinutes(getDayStart(day), minutes).toISOString();
};

// ─── Current time indicator ───────────────────────────────
const now = ref(new Date());
let clockTimer;
onMounted(() => {
  clockTimer = setInterval(() => {
    now.value = new Date();
  }, 30000);
});
onUnmounted(() => {
  clearInterval(clockTimer);
});

const nowTopPx = computed(() => {
  const offset = differenceInMinutes(now.value, getDayStart(now.value));
  if (offset < 0 || offset > TOTAL_HOURS * 60) return null;
  return (offset / 60) * ROW_HEIGHT;
});

// ─── Drag & Drop ──────────────────────────────────────────
// drag = { booking, day, topPx }
const drag = ref(null);

const startDrag = (event, booking, day) => {
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  const initTopPx = toTopPx(booking.starts_at, day);
  const initClientY = event.clientY;
  const maxTopPx = GRID_HEIGHT - toHeightPx(booking);

  drag.value = { booking, day, topPx: initTopPx };
  let hasMoved = false;

  const onMove = e => {
    if (!hasMoved && Math.abs(e.clientY - initClientY) < 5) return;
    hasMoved = true;
    const raw = initTopPx + (e.clientY - initClientY);
    const snapped = Math.round(raw / SNAP_PX) * SNAP_PX;
    drag.value = {
      ...drag.value,
      topPx: Math.max(0, Math.min(snapped, maxTopPx)),
    };
  };

  const onUp = () => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);

    if (!hasMoved) {
      // Pure click — open edit modal
      drag.value = null;
      emit('editBooking', booking);
      return;
    }

    if (!drag.value) return;
    const origTopPx = toTopPx(drag.value.booking.starts_at, drag.value.day);
    if (Math.abs(drag.value.topPx - origTopPx) >= SNAP_PX) {
      const newStartsAt = pxToIso(drag.value.topPx, drag.value.day);
      const dur = differenceInMinutes(
        parseISO(drag.value.booking.ends_at),
        parseISO(drag.value.booking.starts_at)
      );
      const newEndsAt = addMinutes(parseISO(newStartsAt), dur).toISOString();
      emit('moveBooking', {
        id: drag.value.booking.id,
        starts_at: newStartsAt,
        ends_at: newEndsAt,
      });
    }
    drag.value = null;
  };

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
};

const bookingTopPx = (booking, day) =>
  drag.value?.booking.id === booking.id && isSameDay(drag.value.day, day)
    ? drag.value.topPx
    : toTopPx(booking.starts_at, day);

// ─── Column layout (Google Calendar style) ───────────────
// Returns [{ booking, col, totalCols }] for a list of bookings,
// placing overlapping bookings side-by-side.
const computeLayout = bookings => {
  if (!bookings.length) return [];

  const sorted = [...bookings].sort(
    (a, b) => parseISO(a.starts_at) - parseISO(b.starts_at)
  );

  // Assign each booking to the lowest available column
  const colEnds = []; // track the end time of the last booking in each column
  const cols = sorted.map(b => {
    const start = parseISO(b.starts_at);
    const end = parseISO(b.ends_at);
    let c = colEnds.findIndex(e => e <= start);
    if (c === -1) {
      c = colEnds.length;
      colEnds.push(end);
    } else colEnds[c] = end;
    return c;
  });

  // For each booking, find how many columns are used by its overlap cluster
  const totalCols = sorted.map((b, i) => {
    const s = parseISO(b.starts_at);
    const e = parseISO(b.ends_at);
    let max = cols[i];
    sorted.forEach((b2, j) => {
      if (i === j) return;
      if (parseISO(b2.starts_at) < e && parseISO(b2.ends_at) > s) {
        max = Math.max(max, cols[j]);
      }
    });
    return max + 1;
  });

  return sorted.map((booking, i) => ({
    booking,
    col: cols[i],
    totalCols: totalCols[i],
  }));
};

// ─── Calendar data ────────────────────────────────────────
const calendarDays = computed(() => {
  if (props.view === 'month') {
    const s = startOfWeek(startOfMonth(props.anchorDate), { weekStartsOn: 0 });
    const e = endOfWeek(endOfMonth(props.anchorDate), { weekStartsOn: 0 });
    return eachDayOfInterval({ start: s, end: e });
  }
  if (props.view === 'week') {
    const s = startOfWeek(props.anchorDate, { weekStartsOn: 0 });
    const e = endOfWeek(props.anchorDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: s, end: e });
  }
  return [props.anchorDate];
});

const bookingsForDay = day =>
  props.bookings.filter(b => {
    try {
      return isSameDay(parseISO(b.starts_at), day);
    } catch {
      return false;
    }
  });

const availableSlotsForDay = day =>
  props.availability.filter(s => {
    try {
      return isSameDay(parseISO(s.starts_at), day) && s.status === 'available';
    } catch {
      return false;
    }
  }).length;

const hasSlotsForDay = day =>
  props.availability.some(s => {
    try {
      return isSameDay(parseISO(s.starts_at), day);
    } catch {
      return false;
    }
  });

const isBlock = booking => booking.booking_type === 'block';

const bookingClasses = booking => {
  if (isBlock(booking)) return 'bg-slate-100 text-slate-500 border-slate-300';
  return (
    {
      confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      completed: 'bg-blue-50 text-blue-700 border-blue-200',
      cancelled: 'bg-red-50 text-red-600 border-red-200',
      no_show: 'bg-slate-50 text-slate-500 border-slate-200',
    }[booking.status] || 'bg-emerald-50 text-emerald-700 border-emerald-200'
  );
};

// Keep for backwards-compat in month view
const statusClasses = status =>
  ({
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    completed: 'bg-blue-50 text-blue-700 border-blue-200',
    cancelled: 'bg-red-50 text-red-600 border-red-200',
    no_show: 'bg-slate-50 text-slate-500 border-slate-200',
  })[status] || 'bg-emerald-50 text-emerald-700 border-emerald-200';

const STATUS_ACCENT = {
  confirmed: '#10b981',
  pending: '#f59e0b',
  completed: '#3b82f6',
  cancelled: '#ef4444',
  no_show: '#94a3b8',
};
const BLOCK_ACCENT = '#94a3b8';

// Drag-time tooltip: show new time while dragging
const dragTimeLabel = computed(() => {
  if (!drag.value) return '';
  const snapped = Math.round(drag.value.topPx / SNAP_PX) * SNAP_PX;
  const minutes = (snapped / ROW_HEIGHT) * 60;
  const t = addMinutes(getDayStart(drag.value.day), minutes);
  return format(t, 'HH:mm');
});

// ─── AGORA / PRÓXIMO helpers ──────────────────────────────
const isBookingNow = b => {
  try {
    return (
      parseISO(b.starts_at) <= now.value && now.value <= parseISO(b.ends_at)
    );
  } catch {
    return false;
  }
};

// Returns the ID of the next upcoming booking on the given day
const nextBookingIdForDay = day => {
  const dayBkgs = bookingsForDay(day)
    .filter(b => !isBookingNow(b) && parseISO(b.starts_at) > now.value)
    .sort((a, b) => parseISO(a.starts_at) - parseISO(b.starts_at));
  return dayBkgs[0]?.id ?? null;
};
</script>

<template>
  <div
    class="flex flex-col h-full w-full overflow-hidden"
    :class="drag ? 'select-none' : ''"
  >
    <!-- Loading overlay -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-n-background/60 z-30 flex items-center justify-center"
    >
      <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-9" />
    </div>

    <!-- ═══ MONTH VIEW ════════════════════════════════════ -->
    <template v-if="view === 'month'">
      <div
        class="grid grid-cols-7 border-b border-n-weak bg-n-background flex-shrink-0"
      >
        <div
          v-for="label in WEEKDAY_LABELS"
          :key="label"
          class="py-2.5 text-center text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
        >
          {{ label }}
        </div>
      </div>

      <div class="grid grid-cols-7 flex-1 overflow-y-auto">
        <button
          v-for="day in calendarDays"
          :key="day.toISOString()"
          class="border-b border-r border-n-weak p-2 text-left transition-all duration-100 group min-h-[90px] relative"
          :class="[
            !isSameMonth(day, anchorDate)
              ? 'bg-n-alpha-1 opacity-60'
              : 'bg-n-background hover:bg-n-alpha-1',
            selectedDate && isSameDay(day, selectedDate)
              ? 'ring-1 ring-inset ring-n-brand'
              : '',
          ]"
          @click="emit('selectDate', day)"
        >
          <span
            class="inline-flex items-center justify-center w-7 h-7 text-sm font-medium rounded-full transition-colors"
            :class="
              isToday(day)
                ? 'bg-n-brand text-white font-semibold'
                : selectedDate && isSameDay(day, selectedDate)
                  ? 'bg-n-brand/10 text-n-brand font-semibold'
                  : 'text-n-slate-12 group-hover:bg-n-alpha-2'
            "
          >
            {{ format(day, 'd') }}
          </span>

          <span
            v-if="hasSlotsForDay(day) && availableSlotsForDay(day) > 0"
            class="absolute top-2 right-2"
          >
            <span
              class="inline-block w-1.5 h-1.5 rounded-full"
              :style="{ backgroundColor: scheduleColor }"
            />
          </span>

          <div class="mt-1 space-y-0.5">
            <div
              v-for="booking in bookingsForDay(day).slice(0, 2)"
              :key="booking.id"
              class="text-xs px-1.5 py-0.5 rounded-md border font-medium flex items-center gap-1"
              :class="[
                bookingClasses(booking),
                isBookingNow(booking) ? 'ring-1 ring-red-400' : '',
              ]"
            >
              <span
                v-if="isBlock(booking)"
                class="i-lucide-ban size-2.5 flex-shrink-0 opacity-60"
              />
              <span
                v-else-if="isBookingNow(booking)"
                class="animate-pulse w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"
              />
              <span class="truncate">{{ format(parseISO(booking.starts_at), 'HH:mm') }}
                {{
                  isBlock(booking)
                    ? booking.notes || 'Bloqueado'
                    : booking.attendee_name
                }}</span>
            </div>
            <div
              v-if="bookingsForDay(day).length > 2"
              class="text-xs text-n-slate-9 pl-1"
            >
              +{{ bookingsForDay(day).length - 2 }} mais
            </div>
          </div>
        </button>
      </div>
    </template>

    <!-- ═══ WEEK VIEW ════════════════════════════════════ -->
    <template v-else-if="view === 'week'">
      <!-- Day headers -->
      <div
        class="flex border-b border-n-weak bg-n-background flex-shrink-0 sticky top-0 z-10"
      >
        <div class="w-16 flex-shrink-0" />
        <div
          v-for="day in calendarDays"
          :key="day.toISOString()"
          class="flex-1 text-center py-2.5 border-l border-n-weak"
        >
          <div class="text-xs text-n-slate-9 uppercase tracking-wide">
            {{ WEEKDAY_LABELS[day.getDay()] }}
          </div>
          <div
            class="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium mx-auto mt-0.5 cursor-pointer transition-colors"
            :class="
              isToday(day)
                ? 'bg-n-brand text-white font-semibold'
                : selectedDate && isSameDay(day, selectedDate)
                  ? 'bg-n-brand/10 text-n-brand'
                  : 'text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="emit('selectDate', day)"
          >
            {{ format(day, 'd') }}
          </div>
        </div>
      </div>

      <!-- Time grid -->
      <div class="flex flex-1 overflow-y-auto">
        <!-- Hour labels (pixel-aligned) -->
        <div
          class="w-16 flex-shrink-0 relative flex-shrink-0"
          :style="{ height: `${GRID_HEIGHT}px` }"
        >
          <div
            v-for="(hour, i) in hours"
            :key="hour"
            class="absolute w-full border-b border-n-weak"
            :style="{ top: `${i * ROW_HEIGHT}px`, height: `${ROW_HEIGHT}px` }"
          >
            <span
              class="absolute right-2 text-xs text-n-slate-8 leading-none select-none"
              :class="i === 0 ? 'top-1' : 'top-0 -translate-y-1/2'"
            >
              {{ String(hour).padStart(2, '0') }}:00
            </span>
          </div>
        </div>

        <!-- Day columns -->
        <div
          v-for="day in calendarDays"
          :key="day.toISOString()"
          class="flex-1 border-l border-n-weak relative cursor-pointer"
          :class="
            selectedDate && isSameDay(day, selectedDate)
              ? 'bg-n-brand/5'
              : 'hover:bg-n-alpha-1'
          "
          :style="{ height: `${GRID_HEIGHT}px` }"
          @click="emit('selectDate', day)"
        >
          <!-- Hour rows (grid lines only) -->
          <div
            v-for="(hour, i) in hours"
            :key="hour"
            class="absolute w-full border-b border-n-weak/60"
            :style="{ top: `${i * ROW_HEIGHT}px`, height: `${ROW_HEIGHT}px` }"
          >
            <div
              class="absolute inset-x-0 top-1/2 border-t border-dashed border-n-weak/40 pointer-events-none"
            />
          </div>

          <!-- Current time line -->
          <div
            v-if="nowTopPx !== null && isToday(day)"
            class="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
            :style="{ top: `${nowTopPx}px` }"
          >
            <div class="w-2 h-2 rounded-full bg-red-500 -ml-1 flex-shrink-0" />
            <div class="flex-1 border-t border-red-500" />
          </div>

          <!-- Booking cards (column layout) -->
          <div
            v-for="{ booking, col, totalCols } in computeLayout(
              bookingsForDay(day)
            )"
            :key="booking.id"
            class="absolute rounded-md px-1.5 border border-l-[3px] text-xs overflow-hidden z-10"
            :class="[
              bookingClasses(booking),
              drag?.booking.id === booking.id
                ? 'opacity-60 shadow-xl z-30 cursor-grabbing ring-2 ring-n-brand/50'
                : 'cursor-grab shadow-sm hover:shadow-md hover:z-20',
              isBlock(booking) ? 'opacity-70' : '',
            ]"
            :style="{
              top: `${bookingTopPx(booking, day)}px`,
              height: `${toHeightPx(booking)}px`,
              left: `calc(${(col / totalCols) * 100}% + 2px)`,
              width: `calc(${(1 / totalCols) * 100}% - 4px)`,
              borderLeftColor: isBlock(booking)
                ? BLOCK_ACCENT
                : STATUS_ACCENT[booking.status] || STATUS_ACCENT.confirmed,
              backgroundImage: isBlock(booking)
                ? 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)'
                : undefined,
            }"
            @mousedown.stop="startDrag($event, booking, day)"
          >
            <!-- Drag time badge -->
            <div
              v-if="drag?.booking.id === booking.id"
              class="absolute -top-5 left-1/2 -translate-x-1/2 bg-n-slate-12 text-white text-xs px-1.5 py-0.5 rounded font-mono whitespace-nowrap shadow-lg z-40"
            >
              {{ dragTimeLabel }}
            </div>
            <!-- Block label -->
            <template v-if="isBlock(booking)">
              <div
                class="flex items-center gap-0.5 font-medium truncate leading-tight"
              >
                <span class="i-lucide-ban size-2.5 flex-shrink-0" />
                <span class="truncate">{{ booking.notes || 'Bloqueado' }}</span>
              </div>
              <div class="opacity-60 text-[10px]">
                {{ format(parseISO(booking.starts_at), 'HH:mm') }}
              </div>
            </template>
            <!-- Normal booking label -->
            <template v-else>
              <!-- AGORA / PRÓXIMO badge -->
              <div v-if="isToday(day)" class="mb-0.5">
                <span
                  v-if="isBookingNow(booking)"
                  class="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[8px] font-bold bg-red-600 text-white leading-none"
                >
                  <span class="animate-pulse w-1 h-1 rounded-full bg-white" />
                  AO VIVO
                </span>
                <span
                  v-else-if="booking.id === nextBookingIdForDay(day)"
                  class="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold bg-yellow-300 text-yellow-900 border border-yellow-500 leading-none"
                >
                  PRÓXIMO
                </span>
              </div>
              <div class="font-semibold truncate leading-tight">
                {{ booking.attendee_name }}
              </div>
              <div
                class="flex items-center gap-1 opacity-70 truncate text-[10px]"
              >
                <span>{{ format(parseISO(booking.starts_at), 'HH:mm') }}</span>
                <span
                  v-if="booking.slot_quantity > 1"
                  class="bg-black/10 rounded px-0.5 font-medium"
                >
                  ×{{ booking.slot_quantity }}
                </span>
              </div>
              <div
                v-if="booking.metadata?.product"
                class="opacity-60 truncate text-[9px]"
              >
                {{ booking.metadata.product }}
              </div>
              <div
                v-if="booking.metadata?.appointment_type"
                class="opacity-60 truncate text-[9px]"
              >
                {{ booking.metadata.appointment_type }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- ═══ DAY VIEW ═════════════════════════════════════ -->
    <template v-else-if="view === 'day'">
      <!-- Day header -->
      <div
        class="flex border-b border-n-weak bg-n-background flex-shrink-0 px-4 py-3"
      >
        <div class="text-sm font-semibold text-n-slate-12">
          {{ WEEKDAY_LABELS[anchorDate.getDay()] }},
          {{ format(anchorDate, 'dd') }} de
          {{
            [
              'Janeiro',
              'Fevereiro',
              'Março',
              'Abril',
              'Maio',
              'Junho',
              'Julho',
              'Agosto',
              'Setembro',
              'Outubro',
              'Novembro',
              'Dezembro',
            ][anchorDate.getMonth()]
          }}
        </div>
      </div>

      <!-- Time grid -->
      <div class="flex flex-1 overflow-y-auto">
        <!-- Hour labels (pixel-aligned) -->
        <div
          class="w-16 flex-shrink-0 relative"
          :style="{ height: `${GRID_HEIGHT}px` }"
        >
          <div
            v-for="(hour, i) in hours"
            :key="hour"
            class="absolute w-full border-b border-n-weak"
            :style="{ top: `${i * ROW_HEIGHT}px`, height: `${ROW_HEIGHT}px` }"
          >
            <span
              class="absolute right-2 text-xs text-n-slate-8 leading-none select-none"
              :class="i === 0 ? 'top-1' : 'top-0 -translate-y-1/2'"
            >
              {{ String(hour).padStart(2, '0') }}:00
            </span>
          </div>
        </div>

        <!-- Day column -->
        <div
          class="flex-1 border-l border-n-weak relative"
          :style="{ height: `${GRID_HEIGHT}px` }"
        >
          <!-- Hour rows (click to create) -->
          <div
            v-for="(hour, i) in hours"
            :key="hour"
            class="absolute w-full border-b border-n-weak/60 cursor-pointer hover:bg-n-alpha-1"
            :style="{ top: `${i * ROW_HEIGHT}px`, height: `${ROW_HEIGHT}px` }"
            @click="emit('createBooking', { hour })"
          >
            <div
              class="absolute inset-x-0 top-1/2 border-t border-dashed border-n-weak/40 pointer-events-none"
            />
          </div>

          <!-- Current time line -->
          <div
            v-if="nowTopPx !== null && isToday(anchorDate)"
            class="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
            :style="{ top: `${nowTopPx}px` }"
          >
            <div class="w-2 h-2 rounded-full bg-red-500 -ml-1 flex-shrink-0" />
            <div class="flex-1 border-t border-red-500" />
          </div>

          <!-- Availability bands -->
          <div
            v-for="slot in availability"
            :key="slot.starts_at"
            class="absolute left-0 right-0 pointer-events-none opacity-20 rounded-sm"
            :class="
              slot.status === 'available' ? 'bg-emerald-400' : 'bg-n-slate-6'
            "
            :style="{
              top: `${toTopPx(slot.starts_at, anchorDate)}px`,
              height: `${toHeightPx({ starts_at: slot.starts_at, ends_at: slot.ends_at })}px`,
            }"
          />

          <!-- Booking cards (column layout) -->
          <div
            v-for="{ booking, col, totalCols } in computeLayout(
              bookingsForDay(anchorDate)
            )"
            :key="booking.id"
            class="absolute rounded-lg px-2 border border-l-[3px] text-xs overflow-hidden z-10"
            :class="[
              bookingClasses(booking),
              drag?.booking.id === booking.id
                ? 'opacity-60 shadow-xl z-30 cursor-grabbing ring-2 ring-n-brand/50'
                : 'cursor-grab shadow-sm hover:shadow-md hover:z-20',
              isBlock(booking) ? 'opacity-70' : '',
            ]"
            :style="{
              top: `${bookingTopPx(booking, anchorDate)}px`,
              height: `${toHeightPx(booking)}px`,
              left: `calc(${(col / totalCols) * 100}% + 2px)`,
              width: `calc(${(1 / totalCols) * 100}% - 4px)`,
              borderLeftColor: isBlock(booking)
                ? BLOCK_ACCENT
                : STATUS_ACCENT[booking.status] || STATUS_ACCENT.confirmed,
              backgroundImage: isBlock(booking)
                ? 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)'
                : undefined,
            }"
            @mousedown.stop="startDrag($event, booking, anchorDate)"
          >
            <!-- Drag time badge -->
            <div
              v-if="drag?.booking.id === booking.id"
              class="absolute -top-5 left-1/2 -translate-x-1/2 bg-n-slate-12 text-white text-xs px-1.5 py-0.5 rounded font-mono whitespace-nowrap shadow-lg z-40"
            >
              {{ dragTimeLabel }}
            </div>
            <!-- Block label -->
            <template v-if="isBlock(booking)">
              <div class="flex items-center gap-1 font-medium mt-0.5">
                <span class="i-lucide-ban size-3 flex-shrink-0" />
                <span class="truncate">{{ booking.notes || 'Bloqueado' }}</span>
              </div>
              <div class="opacity-60 text-[11px]">
                {{ format(parseISO(booking.starts_at), 'HH:mm') }} –
                {{ format(parseISO(booking.ends_at), 'HH:mm') }}
              </div>
              <div
                v-if="booking.resource"
                class="opacity-50 truncate text-[10px]"
              >
                {{ booking.resource.name }}
              </div>
            </template>
            <!-- Normal booking label -->
            <template v-else>
              <!-- AGORA / PRÓXIMO badge -->
              <div v-if="isToday(anchorDate)" class="mt-0.5 mb-0.5">
                <span
                  v-if="isBookingNow(booking)"
                  class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white leading-none"
                >
                  <span class="animate-pulse w-1 h-1 rounded-full bg-white" />
                  AO VIVO
                </span>
                <span
                  v-else-if="booking.id === nextBookingIdForDay(anchorDate)"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-yellow-300 text-yellow-900 border border-yellow-500 leading-none"
                >
                  PRÓXIMO
                </span>
              </div>
              <div class="font-semibold truncate">
                {{ booking.attendee_name }}
              </div>
              <div class="flex items-center gap-1.5 opacity-70 text-[11px]">
                <span>{{ format(parseISO(booking.starts_at), 'HH:mm') }} –
                  {{ format(parseISO(booking.ends_at), 'HH:mm') }}</span>
                <span
                  v-if="booking.slot_quantity > 1"
                  class="bg-black/10 rounded px-1 font-medium"
                >
                  ×{{ booking.slot_quantity }} vagas
                </span>
              </div>
              <div
                v-if="booking.metadata?.product"
                class="opacity-70 truncate text-[10px] font-medium"
              >
                {{ booking.metadata.product }}
              </div>
              <div
                v-if="booking.metadata?.appointment_type"
                class="opacity-70 truncate text-[10px] font-medium"
              >
                {{ booking.metadata.appointment_type }}
              </div>
              <div
                v-if="booking.resource"
                class="opacity-60 truncate text-[10px]"
              >
                {{ booking.resource.name }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
